import React, { FC, ReactNode, useEffect, useMemo, useRef, useState } from 'react'
import { Badge, Box, Button, Chip, FormControl, Grid, InputLabel, MenuItem, OutlinedInput, Select, SelectChangeEvent, SxProps, Typography } from '@mui/material'
import TuneIcon from '@mui/icons-material/Tune'
import debounce from 'lodash/debounce'
import { useFormik, FormikProvider, Form } from 'formik'
import { useLocation } from 'react-router-dom'
import { ActiveFilterChips, AutocompleteTextField, FilterDrawer, FormCheckbox, NumberTextField, TextField } from 'src/components/ui'
import { useQueryParams } from 'src/hooks/ui'
import { SearchParamsKeys, SensorFilters, SensorStateList } from 'src/enums'
import { DefaultsEntry } from 'src/components/providers/queryParamsProvider'
import { Contractor } from 'src/api'
import { useSensorFirmwareVersions } from 'src/hooks/api'
import { OrderDateRangeFilter } from './orderDateRangeFilter'
import { buildSensorFilterChips, getSensorFilterParamKeys, sensorReadingOptions } from './sensorFilterChips'

const whenUnusedSensor: DefaultsEntry['when'] = (params, pathname) => pathname === '/sensors' && params[SearchParamsKeys.State] === SensorStateList.UnusedSensor

export const sensorListQueryParamDefaults: DefaultsEntry[] = [
  {
    queryValues: { [SearchParamsKeys.ExcludeStaging]: 'true' },
    when: whenUnusedSensor
  },
  {
    queryValues: { [SearchParamsKeys.LastPingMoreThan]: '180' },
    when: whenUnusedSensor
  }
]

// See .claude/rules/list-filters.md: at most 3 filters render inline (the Order number/Part
// group and Order date count and take the first slots); with more than 3 in total, the rest
// move to the "Filters" drawer. With 3 or fewer everything stays inline and there is no drawer.
export const INLINE_FILTERS_LIMIT = 3

const FILTER_SECTIONS: { title: string; filters: SensorFilters[] }[] = [
  {
    title: 'Health',
    filters: [
      SensorFilters.LowBattery,
      SensorFilters.WeakSignal,
      SensorFilters.LastPingMoreThanHour,
      SensorFilters.SyncPeriod,
      SensorFilters.ExcludeStaging,
      SensorFilters.LastPingMoreThan
    ]
  },
  { title: 'Readings', filters: [SensorFilters.ReadingsAge, SensorFilters.AvailabilityInSystem] },
  {
    title: 'Configuration',
    filters: [
      SensorFilters.AvailableInThingspace,
      SensorFilters.CurrentSensorConfiguration,
      SensorFilters.BleStatus,
      SensorFilters.FirmwareVersion,
      SensorFilters.ActivationStatus
    ]
  },
  { title: 'Contractor', filters: [SensorFilters.Contractor] }
]

interface SensorListFiltersProps {
  currentContractor?: Contractor | null
  contractors?: Contractor[]
  contractorsLoading?: boolean
  findContractorsByName?: (name: string) => void
  activeFilters?: SensorFilters[]
}

export const SensorListFilters: FC<SensorListFiltersProps> = ({
  contractors = [],
  contractorsLoading = false,
  findContractorsByName = () => {},
  currentContractor = null,
  activeFilters = []
}) => {
  const [queryParams, setQueryParams, removeQueryParams] = useQueryParams()
  const { pathname } = useLocation()
  const [isDrawerOpen, setIsDrawerOpen] = useState(false)
  const { data } = useSensorFirmwareVersions()
  const sensorFirmwareVersions = useMemo(() => {
    const list = data || []
    return [{ name: 'All', value: 'all' }, ...list.map((item) => ({ name: item, value: item }))]
  }, [data])
  const [hasContractorFilter] = useState(activeFilters.indexOf(SensorFilters.Contractor) !== -1)
  const [hasBatteryLevelFilter] = useState(activeFilters.indexOf(SensorFilters.LowBattery) !== -1)
  const [hasSignalLevelFilter] = useState(activeFilters.indexOf(SensorFilters.WeakSignal) !== -1)
  const [hasLastPingFilter] = useState(activeFilters.indexOf(SensorFilters.LastPingMoreThanHour) !== -1)
  const [hasLastPingMoreThanFilter] = useState(activeFilters.indexOf(SensorFilters.LastPingMoreThan) !== -1)
  const [hasExcludeStagingFilter] = useState(activeFilters.indexOf(SensorFilters.ExcludeStaging) !== -1)

  const [hasSyncPeriodFilter] = useState(activeFilters.indexOf(SensorFilters.SyncPeriod) !== -1)
  const [hasReadingsAgeFilter] = useState(activeFilters.indexOf(SensorFilters.ReadingsAge) !== -1)
  const [hasAvailabilityInSystemFilter] = useState(activeFilters.indexOf(SensorFilters.AvailabilityInSystem) !== -1)
  const [hasAvailableInThingspaceFilter] = useState(activeFilters.indexOf(SensorFilters.AvailableInThingspace) !== -1)
  const [hasSensorCurrentConfigFilter] = useState(activeFilters.indexOf(SensorFilters.CurrentSensorConfiguration) !== -1)
  const [hasBleStatusFilter] = useState(activeFilters.indexOf(SensorFilters.BleStatus) !== -1)
  const [hasSensorFirmwareVersionFilter] = useState(activeFilters.indexOf(SensorFilters.FirmwareVersion) !== -1)
  const [hasActivationStatusFilter] = useState(activeFilters.indexOf(SensorFilters.ActivationStatus) !== -1)
  const [hasOrderNumberFilter] = useState(activeFilters.indexOf(SensorFilters.OrderNumber) !== -1)
  const [hasOrderDateFilter] = useState(activeFilters.indexOf(SensorFilters.OrderDate) !== -1)

  const formik = useFormik({
    enableReinitialize: true,
    initialValues: {
      ...(hasContractorFilter
        ? {
            [SearchParamsKeys.Contractor]: queryParams[SearchParamsKeys.Contractor] || null
          }
        : null),
      ...(hasBatteryLevelFilter
        ? {
            [SearchParamsKeys.LowBattery]: queryParams[SearchParamsKeys.LowBattery] || false
          }
        : null),
      ...(hasSignalLevelFilter
        ? {
            [SearchParamsKeys.WeakSignal]: queryParams[SearchParamsKeys.WeakSignal] || false
          }
        : null),
      ...(hasLastPingFilter
        ? {
            [SearchParamsKeys.LastPingMoreThanHour]: queryParams[SearchParamsKeys.LastPingMoreThanHour] || false
          }
        : null),
      ...(hasLastPingMoreThanFilter
        ? {
            [SearchParamsKeys.LastPingMoreThan]: queryParams[SearchParamsKeys.LastPingMoreThan] || null
          }
        : null),
      ...(hasExcludeStagingFilter
        ? {
            [SearchParamsKeys.ExcludeStaging]: queryParams[SearchParamsKeys.ExcludeStaging] === 'true'
          }
        : null),
      ...(hasSyncPeriodFilter
        ? {
            [SearchParamsKeys.SyncPeriodLessThanHour]: queryParams[SearchParamsKeys.SyncPeriodLessThanHour] || false
          }
        : null),
      ...(hasReadingsAgeFilter
        ? {
            [SearchParamsKeys.AgeOfReadingsMoreThanHour]: queryParams[SearchParamsKeys.AgeOfReadingsMoreThanHour]?.split(',') || []
          }
        : null),
      ...(hasAvailabilityInSystemFilter
        ? {
            [SearchParamsKeys.IsAvailableInSystem]: queryParams[SearchParamsKeys.IsAvailableInSystem] || null
          }
        : null),
      ...(hasAvailableInThingspaceFilter
        ? {
            [SearchParamsKeys.IsAvailableInThingspace]: queryParams[SearchParamsKeys.IsAvailableInThingspace] || 'all'
          }
        : null),
      ...(hasSensorCurrentConfigFilter
        ? {
            [SearchParamsKeys.CurrentSensorConfiguration]: queryParams[SearchParamsKeys.CurrentSensorConfiguration] || null
          }
        : null),
      ...(hasBleStatusFilter
        ? {
            [SearchParamsKeys.IsBleEnabled]: queryParams[SearchParamsKeys.IsBleEnabled] || 'all'
          }
        : null),
      ...(hasSensorFirmwareVersionFilter
        ? {
            [SearchParamsKeys.SensorFirmwareVersion]: queryParams[SearchParamsKeys.SensorFirmwareVersion] || 'all'
          }
        : null),
      ...(hasActivationStatusFilter
        ? {
            [SearchParamsKeys.ActivationStatus]: queryParams[SearchParamsKeys.ActivationStatus] || 'all'
          }
        : null),
      ...(hasOrderNumberFilter
        ? {
            [SearchParamsKeys.OrderNumber]: queryParams[SearchParamsKeys.OrderNumber] || null,
            [SearchParamsKeys.OrderPart]: queryParams[SearchParamsKeys.OrderPart] || null
          }
        : null)
    },
    onSubmit: async (formState) => {
      const contractor = formState[SearchParamsKeys.Contractor] || null
      const batteryLevel = formState[SearchParamsKeys.LowBattery] || false
      const signalStrength = formState[SearchParamsKeys.WeakSignal] || false
      const lastPing = formState[SearchParamsKeys.LastPingMoreThanHour] || false
      const lastPingMoreThan = formState[SearchParamsKeys.LastPingMoreThan] || null
      const excludeStaging = formState[SearchParamsKeys.ExcludeStaging] || false
      const syncPeriod = formState[SearchParamsKeys.SyncPeriodLessThanHour] || false
      const readingsAge = formState[SearchParamsKeys.AgeOfReadingsMoreThanHour] || []
      const isAvailableInSystem = formState[SearchParamsKeys.IsAvailableInSystem] || null
      const isAvailableInThingspace = formState[SearchParamsKeys.IsAvailableInThingspace] || 'all'
      const sensorCurrentConfiguration = formState[SearchParamsKeys.CurrentSensorConfiguration] || null
      const bleStatus = formState[SearchParamsKeys.IsBleEnabled] === 'all' ? null : formState[SearchParamsKeys.IsBleEnabled]
      const firmwareVersion = formState[SearchParamsKeys.SensorFirmwareVersion] === 'all' ? null : formState[SearchParamsKeys.SensorFirmwareVersion]
      const activationStatus = formState[SearchParamsKeys.ActivationStatus] || 'all'
      const orderNumber = formState[SearchParamsKeys.OrderNumber] || null
      const orderPart = formState[SearchParamsKeys.OrderPart] || null

      await setQueryParams(
        {
          ...(contractor && hasContractorFilter ? { [SearchParamsKeys.Contractor]: `${contractor}` } : {}),
          ...(batteryLevel && hasBatteryLevelFilter ? { [SensorFilters.LowBattery]: `${batteryLevel}` } : {}),
          ...(signalStrength && hasSignalLevelFilter ? { [SensorFilters.WeakSignal]: `${signalStrength}` } : {}),
          ...(lastPing && hasLastPingFilter ? { [SearchParamsKeys.LastPingMoreThanHour]: `${lastPing}` } : {}),
          ...(lastPingMoreThan && hasLastPingMoreThanFilter ? { [SearchParamsKeys.LastPingMoreThan]: `${lastPingMoreThan}` } : {}),
          ...(hasExcludeStagingFilter ? { [SearchParamsKeys.ExcludeStaging]: `${excludeStaging}` } : {}),
          ...(syncPeriod && hasSyncPeriodFilter ? { [SearchParamsKeys.SyncPeriodLessThanHour]: `${syncPeriod}` } : {}),
          ...(readingsAge.length > 0 && hasReadingsAgeFilter ? { [SearchParamsKeys.AgeOfReadingsMoreThanHour]: readingsAge.join(',') } : {}),
          ...(isAvailableInSystem && hasAvailabilityInSystemFilter ? { [SearchParamsKeys.IsAvailableInSystem]: `${isAvailableInSystem}` } : {}),
          ...(hasAvailableInThingspaceFilter ? { [SearchParamsKeys.IsAvailableInThingspace]: isAvailableInThingspace } : {}),
          ...(sensorCurrentConfiguration && hasSensorCurrentConfigFilter ? { [SearchParamsKeys.CurrentSensorConfiguration]: `${sensorCurrentConfiguration}` } : {}),
          ...(bleStatus && hasBleStatusFilter ? { [SearchParamsKeys.IsBleEnabled]: `${bleStatus}` } : {}),
          ...(firmwareVersion && hasSensorFirmwareVersionFilter ? { [SearchParamsKeys.SensorFirmwareVersion]: `${firmwareVersion}` } : {}),
          ...(hasActivationStatusFilter ? { [SearchParamsKeys.ActivationStatus]: activationStatus } : {}),
          ...(orderNumber && hasOrderNumberFilter ? { [SearchParamsKeys.OrderNumber]: `${orderNumber}` } : {}),
          ...(orderPart && hasOrderNumberFilter ? { [SearchParamsKeys.OrderPart]: `${orderPart}` } : {})
        },
        [
          SearchParamsKeys.Type,
          SearchParamsKeys.Contractor,
          SearchParamsKeys.LowBattery,
          SearchParamsKeys.WeakSignal,
          SearchParamsKeys.LastPingMoreThanHour,
          SearchParamsKeys.LastPingMoreThan,
          SearchParamsKeys.ExcludeStaging,
          SearchParamsKeys.SyncPeriodLessThanHour,
          SearchParamsKeys.AgeOfReadingsMoreThanHour,
          SearchParamsKeys.OrderNumber,
          SearchParamsKeys.OrderPart
        ]
      )
    }
  })

  const { submitForm, values, setValues, setFieldValue } = formik

  // Text filters write to the URL (and refetch) only after the user pauses typing
  const submitFormRef = useRef(submitForm)
  submitFormRef.current = submitForm
  const debouncedSubmit = useMemo(() => debounce(() => submitFormRef.current(), 400), [])
  useEffect(() => () => debouncedSubmit.cancel(), [debouncedSubmit])

  const availabilityInSystemOptions = [
    { name: 'All', value: null },
    { name: 'Available', value: true },
    { name: 'Not available', value: false }
  ]
  const availableInThingspaceOptions = [
    { name: 'All', value: 'all' },
    { name: 'Available', value: 'true' },
    { name: 'Not available', value: 'false' }
  ]
  const sensorCurrentConfigurationOptions = [
    { name: 'All', value: 'all' },
    { name: 'Default', value: 'default' },
    { name: 'Custom', value: 'custom' }
  ]
  const activationStatusOptions = [
    { name: 'All', value: 'all' },
    { name: 'Active', value: 'active' },
    { name: 'Deactive', value: 'deactive' }
  ]
  const bleStatusOptions = [
    { name: 'All', value: 'all' },
    { name: 'Enabled', value: 'true' },
    { name: 'Disabled', value: 'false' }
  ]
  const handleSelectChange = (event: SelectChangeEvent<typeof values['ageOfReadingsMoreThanHour']>) => {
    const {
      target: { value }
    } = event
    setValues({
      ageOfReadingsMoreThanHour: typeof value === 'string' ? value.split(',') : value
    })
  }
  const handleSelectClose = () => submitForm()

  const handleAvailabilityChanges = (event: SelectChangeEvent<typeof values['isAvailableInSystem']>) => {
    const {
      target: { value }
    } = event
    setValues({
      isAvailableInSystem: `${value}`,
      ageOfReadingsMoreThanHour: queryParams[SearchParamsKeys.AgeOfReadingsMoreThanHour]?.split(',') || []
    })
    submitForm()
  }

  const handleAvailableInThingspaceChanges = (event: SelectChangeEvent<typeof values['isAvailableInThingspace']>) => {
    const {
      target: { value }
    } = event
    setFieldValue(SearchParamsKeys.IsAvailableInThingspace, value)
    submitForm()
  }

  const handleSensorCurrentConfigChanges = (event: SelectChangeEvent<typeof values['currentSensorConfiguration']>) => {
    const {
      target: { value }
    } = event
    setValues({
      currentSensorConfiguration: value,
      ageOfReadingsMoreThanHour: queryParams[SearchParamsKeys.AgeOfReadingsMoreThanHour]?.split(',') || []
    })
    submitForm()
  }
  const handleBleStatusChanges = async (event: SelectChangeEvent<typeof values['isBleEnabled']>) => {
    const {
      target: { value }
    } = event
    let _value = value
    setFieldValue(SearchParamsKeys.IsBleEnabled, _value)
    if (value === 'all') {
      await removeQueryParams([SearchParamsKeys.IsBleEnabled])
    } else {
      submitForm()
    }
  }
  const handleSensorFirmwareVersionChanges = async (event: SelectChangeEvent<typeof values['sensorFirmwareVersion']>) => {
    const {
      target: { value }
    } = event
    let _value = value
    setFieldValue(SearchParamsKeys.SensorFirmwareVersion, _value)
    if (value === 'all') {
      await removeQueryParams([SearchParamsKeys.SensorFirmwareVersion])
    } else {
      submitForm()
    }
  }
  const handleActivationStatusChanges = (event: SelectChangeEvent<typeof values['activationStatus']>) => {
    const {
      target: { value }
    } = event
    setFieldValue(SearchParamsKeys.ActivationStatus, value)
    submitForm()
  }

  // Inline vs drawer split — see .claude/rules/list-filters.md.
  // The order controls occupy the first inline slots; remaining slots go to the
  // tab's other filters in activeFilters order.
  const orderFiltersCount = Number(hasOrderNumberFilter) + Number(hasOrderDateFilter)
  const otherFilters = activeFilters.filter((filter) => filter !== SensorFilters.OrderNumber && filter !== SensorFilters.OrderDate)
  const totalFiltersCount = orderFiltersCount + otherFilters.length
  const inlineOthersCount = totalFiltersCount <= INLINE_FILTERS_LIMIT ? otherFilters.length : Math.max(0, INLINE_FILTERS_LIMIT - orderFiltersCount)
  const inlineFilters = otherFilters.slice(0, inlineOthersCount)
  const drawerFilters = otherFilters.slice(inlineOthersCount)

  // On the unused-sensor tab lastPingMoreThan is a sticky tab default — removal would re-apply it
  const stickyKeys = whenUnusedSensor(queryParams, pathname) ? [SearchParamsKeys.LastPingMoreThan as string] : []
  const chipDescriptors = buildSensorFilterChips(queryParams, drawerFilters, { contractorName: currentContractor?.name, stickyKeys })
  const chipItems = chipDescriptors.map((chip) => ({
    id: chip.id,
    label: chip.label,
    onRemove: chip.removable
      ? () => {
          if (chip.setValues) setQueryParams(chip.setValues as Record<string, string>)
          else if (chip.clearKeys) removeQueryParams(chip.clearKeys)
        }
      : undefined
  }))
  const handleClearAll = () => removeQueryParams(getSensorFilterParamKeys(drawerFilters))

  const selectSx = (inDrawer: boolean): SxProps => (inDrawer ? { width: '100%', my: 1 } : { m: 1, width: { xs: '100%', sm: 400 } })

  const renderFilterControl = (filter: SensorFilters, inDrawer: boolean): ReactNode => {
    switch (filter) {
      case SensorFilters.LowBattery:
        return <FormCheckbox view="switch" label="Low battery" name={SearchParamsKeys.LowBattery} onChange={submitForm} />
      case SensorFilters.WeakSignal:
        return <FormCheckbox view="switch" label="Weak signal" name={SearchParamsKeys.WeakSignal} onChange={submitForm} />
      case SensorFilters.LastPingMoreThanHour:
        return <FormCheckbox view="switch" label="Last ping more than hour ago" name={SearchParamsKeys.LastPingMoreThanHour} onChange={submitForm} />
      case SensorFilters.SyncPeriod:
        return <FormCheckbox view="switch" label="Synchronization period less than hour" name={SearchParamsKeys.SyncPeriodLessThanHour} onChange={submitForm} />
      case SensorFilters.ExcludeStaging:
        return <FormCheckbox view="switch" label="Exclude staging sensors" name={SearchParamsKeys.ExcludeStaging} onChange={submitForm} />
      case SensorFilters.LastPingMoreThan:
        return (
          <TextField
            name={SearchParamsKeys.LastPingMoreThan}
            label="Days since a sensor last pinged"
            TextFieldProps={{
              InputProps: {
                inputComponent: NumberTextField as any
              }
            }}
            onChange={debouncedSubmit}
          />
        )
      case SensorFilters.ReadingsAge:
        return (
          <FormControl sx={selectSx(inDrawer)}>
            <InputLabel id="readings-multiple-chip-label">Readings</InputLabel>
            <Select
              labelId="readings-multiple-chip-label"
              id="readings-multiple-chip"
              multiple
              value={values.ageOfReadingsMoreThanHour}
              onChange={handleSelectChange}
              onClose={handleSelectClose}
              input={<OutlinedInput id="select-multiple-chip" label="Readings" />}
              renderValue={(selected) => (
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                  {selected.map((value) => {
                    const reading = sensorReadingOptions.find((item) => item?.value === value)
                    return <Chip key={reading?.value} label={reading?.name} />
                  })}
                </Box>
              )}
            >
              {sensorReadingOptions.map((reading) => (
                <MenuItem key={reading?.value} value={reading?.value}>
                  {reading?.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        )
      case SensorFilters.AvailabilityInSystem:
        return (
          <FormControl sx={selectSx(inDrawer)}>
            <InputLabel id="availability-in-system-chip-label">Availability in the system</InputLabel>
            <Select
              labelId="availability-in-system-chip-label"
              id="availability-in-system-chip"
              value={values[SearchParamsKeys.IsAvailableInSystem] || ''}
              onChange={handleAvailabilityChanges}
              input={<OutlinedInput id="select-chip" label="Availability in the system" />}
              renderValue={(selected) => {
                const availabilityItem = availabilityInSystemOptions.find((item) => `${item.value}` === selected)
                return (
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                    <Chip key={`${availabilityItem?.value}`} label={availabilityItem?.name} />
                  </Box>
                )
              }}
            >
              {availabilityInSystemOptions.map((availabilityItem) => (
                <MenuItem key={`${availabilityItem?.value}`} value={`${availabilityItem?.value}`}>
                  {availabilityItem?.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        )
      case SensorFilters.AvailableInThingspace:
        return (
          <FormControl sx={selectSx(inDrawer)}>
            <InputLabel id="available-in-thingspace-chip-label">Available in Thingspace</InputLabel>
            <Select
              labelId="available-in-thingspace-chip-label"
              id="available-in-thingspace-chip"
              value={values[SearchParamsKeys.IsAvailableInThingspace] || 'all'}
              onChange={handleAvailableInThingspaceChanges}
              input={<OutlinedInput id="select-chip" label="Availability in the system" />}
              renderValue={(selected) => {
                const selectedItem = availableInThingspaceOptions.find((item) => `${item.value}` === selected)
                return (
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                    <Chip key={`${selectedItem?.value}`} label={selectedItem?.name} />
                  </Box>
                )
              }}
            >
              {availableInThingspaceOptions.map((item) => (
                <MenuItem key={`${item?.value}`} value={`${item?.value}`}>
                  {item?.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        )
      case SensorFilters.CurrentSensorConfiguration:
        return (
          <FormControl sx={selectSx(inDrawer)}>
            <InputLabel id="sensor-current-config-chip-label">Sensor current configuration</InputLabel>
            <Select
              labelId="sensor-current-config-chip-label"
              id="sensor-current-config-chip"
              value={values[SearchParamsKeys.CurrentSensorConfiguration] || ''}
              onChange={handleSensorCurrentConfigChanges}
              input={<OutlinedInput id="sensor-current-config-select-chip" label="Sensor current configuration" />}
              renderValue={(selected) => {
                const sensorCurrentConfigItem = sensorCurrentConfigurationOptions.find((item) => `${item.value}` === selected)
                return (
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                    <Chip key={`${sensorCurrentConfigItem?.value}`} label={sensorCurrentConfigItem?.name} />
                  </Box>
                )
              }}
            >
              {sensorCurrentConfigurationOptions.map((sensorCurrentConfigItem) => (
                <MenuItem key={`${sensorCurrentConfigItem?.value}`} value={`${sensorCurrentConfigItem?.value}`}>
                  {sensorCurrentConfigItem?.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        )
      case SensorFilters.BleStatus:
        return (
          <FormControl sx={selectSx(inDrawer)}>
            <InputLabel id="ble-status-chip-label">BLE status</InputLabel>
            <Select
              labelId="ble-status-chip-label"
              id="ble-status-chip"
              value={values[SearchParamsKeys.IsBleEnabled] || ''}
              onChange={handleBleStatusChanges}
              input={<OutlinedInput id="ble-status-select-chip" label="BLE status" />}
              renderValue={(selected) => {
                const bleStatusItem = bleStatusOptions.find((item) => `${item.value}` === selected)
                return (
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                    <Chip key={`${bleStatusItem?.value}`} label={bleStatusItem?.name} />
                  </Box>
                )
              }}
            >
              {bleStatusOptions.map((bleStatusItem) => (
                <MenuItem key={`${bleStatusItem?.value}`} value={`${bleStatusItem?.value}`}>
                  {bleStatusItem?.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        )
      case SensorFilters.FirmwareVersion:
        return (
          <FormControl sx={selectSx(inDrawer)}>
            <InputLabel id="sensor-firmware-version-chip-label">Sensor firmware version</InputLabel>
            <Select
              labelId="sensor-firmware-version-chip-label"
              id="sensor-firmware-version-chip"
              value={values[SearchParamsKeys.SensorFirmwareVersion] || ''}
              onChange={handleSensorFirmwareVersionChanges}
              input={<OutlinedInput id="sensor-firmware-version-select-chip" label="Sensor firmware version" />}
              renderValue={(selected) => {
                const sensorFirmwareVersionItem = sensorFirmwareVersions.find((item) => item.value === selected)
                return (
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                    <Chip key={sensorFirmwareVersionItem?.value} label={sensorFirmwareVersionItem?.name} />
                  </Box>
                )
              }}
            >
              {sensorFirmwareVersions.map((item) => (
                <MenuItem key={item.value} value={item.value}>
                  {item.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        )
      case SensorFilters.ActivationStatus:
        return (
          <FormControl sx={selectSx(inDrawer)}>
            <InputLabel id="activation-status-chip-label">Activation status</InputLabel>
            <Select
              labelId="activation-status-chip-label"
              id="activation-status-chip"
              value={values[SearchParamsKeys.ActivationStatus] || 'all'}
              onChange={handleActivationStatusChanges}
              input={<OutlinedInput id="activation-status-select-chip" label="Activation status" />}
              renderValue={(selected) => {
                const item = activationStatusOptions.find((o) => o.value === selected)
                return (
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                    <Chip key={item?.value} label={item?.name} />
                  </Box>
                )
              }}
            >
              {activationStatusOptions.map((item) => (
                <MenuItem key={item.value} value={item.value}>
                  {item.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        )
      case SensorFilters.Contractor:
        return (
          <AutocompleteTextField
            label="Contractor"
            name={SearchParamsKeys.Contractor}
            labelField="name"
            valueField="id"
            options={contractors}
            onInputChange={findContractorsByName}
            isLoading={contractorsLoading}
            onChange={submitForm}
            initialValue={currentContractor}
          />
        )
      default:
        return null
    }
  }

  const drawerSections = FILTER_SECTIONS.map((section) => ({
    ...section,
    filters: section.filters.filter((filter) => drawerFilters.includes(filter))
  })).filter((section) => section.filters.length > 0)

  return (
    <FormikProvider value={formik}>
      <Form>
        <Grid container alignItems="center" spacing={2} sx={{ minHeight: 64 }}>
          {hasOrderNumberFilter && (
            <Grid item xs={12} sm="auto">
              {/* One visual input group: the fields share a border line as the divider */}
              <Grid container alignItems="flex-start" flexWrap="nowrap">
                <TextField
                  name={SearchParamsKeys.OrderNumber}
                  label="Order number"
                  onChange={debouncedSubmit}
                  TextFieldProps={{
                    size: 'small',
                    sx: { width: { xs: 'calc(100% - 80px)', sm: 200 }, '& .MuiOutlinedInput-root': { borderTopRightRadius: 0, borderBottomRightRadius: 0 } }
                  }}
                />
                <TextField
                  name={SearchParamsKeys.OrderPart}
                  label="Part"
                  onChange={debouncedSubmit}
                  TextFieldProps={{
                    size: 'small',
                    InputProps: {
                      inputComponent: NumberTextField as any
                    },
                    sx: { width: 80, ml: '-1px', '& .MuiOutlinedInput-root': { borderTopLeftRadius: 0, borderBottomLeftRadius: 0 } }
                  }}
                />
              </Grid>
            </Grid>
          )}
          {hasOrderDateFilter && (
            <Grid item xs={12} sm="auto" sx={{ '& .MuiTextField-root': { width: { xs: '100%', sm: 230 } } }}>
              <OrderDateRangeFilter showPresets={false} />
            </Grid>
          )}
          {inlineFilters.map((filter) => (
            <Grid item key={filter} xs={12} sm="auto">
              {renderFilterControl(filter, false)}
            </Grid>
          ))}
          {drawerFilters.length > 0 && (
            <Grid item>
              <Badge badgeContent={chipItems.length} color="primary">
                <Button variant="outlined" startIcon={<TuneIcon />} onClick={() => setIsDrawerOpen(true)} sx={{ minHeight: 40 }}>
                  Filters
                </Button>
              </Badge>
            </Grid>
          )}
        </Grid>
        <ActiveFilterChips chips={chipItems} onClearAll={handleClearAll} />
        {drawerFilters.length > 0 && (
          <FilterDrawer open={isDrawerOpen} onClose={() => setIsDrawerOpen(false)}>
            <Grid container spacing={1}>
              {drawerSections.map((section) => (
                <React.Fragment key={section.title}>
                  <Grid item xs={12}>
                    <Typography variant="overline" color="text.secondary">
                      {section.title}
                    </Typography>
                  </Grid>
                  {section.filters.map((filter) => (
                    <Grid item key={filter} xs={12}>
                      {renderFilterControl(filter, true)}
                    </Grid>
                  ))}
                </React.Fragment>
              ))}
            </Grid>
          </FilterDrawer>
        )}
      </Form>
    </FormikProvider>
  )
}
